// Rotas administrativas: revendas, domínios, caixas, aliases, quarentena.
//
// FRONTEIRA ENTRE CLIENTES. Nenhuma destas funções manda escopo: quem decide
// o que cada um alcança é o SERVIDOR, em toda consulta (api/interno/web/escopo.go).
// A interface usa o nível só para escolher o que desenhar — mostrar um botão a
// mais aqui não dá acesso a nada, e esconder um não protege nada.
//
// Recurso de outro cliente responde 404, nunca 403: um 403 confirmaria que
// existe, e bastaria varrer uuids para mapear a base alheia. A interface
// precisa tratar esse 404 como "não existe", sem inventar mensagem de
// permissão.

import { api } from "./cliente";
import type {
  Alias,
  AliasEntrada,
  Caixa,
  CaixaEntrada,
  DiagnosticoDns,
  Dominio,
  DominioEntrada,
  EventoEntrega,
  ItemFila,
  MensagemQuarentena,
  PaginaDe,
  RegistroAuditoria,
  Revenda,
  RevendaEntrada,
  ServidorOrigem,
  ServidorOrigemEntrada,
  TrabalhoMigracao,
  MaildirOrfao,
  Sessao,
  AntispamDominio,
} from "./tipos";

// `type` e não `interface`: interface não ganha index signature implícita, e
// sem ela o TypeScript recusa passá-la onde se espera um dicionário de
// parâmetros de consulta. Não é preferência de estilo.
type Paginacao = {
  pagina?: number;
  por_pagina?: number;
}

export const revendas = {
  listar: (p?: Paginacao) => api.get<PaginaDe<Revenda>>("/revendas", { consulta: p }),
  ver: (uuid: string) => api.get<Revenda>(`/revendas/${uuid}`),
  criar: (dados: RevendaEntrada) => api.post<Revenda>("/revendas", dados),
  alterar: (uuid: string, dados: Partial<RevendaEntrada>) =>
    api.patch<Revenda>(`/revendas/${uuid}`, dados),
  // Sem confirmação digitada, ao contrário de domínio e caixa: o servidor só
  // remove revenda VAZIA (409 enquanto houver domínio, e RESTRICT no banco
  // atrás disso). Não há e-mail a perder aqui, e pedir para digitar o nome
  // seria cerimônia — que ensina a digitar sem ler, justamente onde a
  // confirmação precisa significar alguma coisa.
  remover: (uuid: string) => api.delete<void>(`/revendas/${uuid}`),
};

/** Domínio com DNS apontando para cá e sem certificado emitido. */
export interface PendenciaSSL {
  uuid: string;
  dominio: string;
  motivo: string;
}

export const dominios = {
  listar: (p?: Paginacao & { revenda?: string }) =>
    api.get<PaginaDe<Dominio>>("/dominios", { consulta: p }),

  /**
   * Os domínios que precisam de certificado AGORA.
   *
   * Falhar aqui não pode quebrar a tela de domínios: isto é um aviso a mais,
   * e tela que não abre por causa de um aviso é pior que aviso nenhum.
   */
  pendenciasSSL: async (): Promise<PendenciaSSL[]> => {
    try {
      const r = await api.get<{ itens?: PendenciaSSL[] }>("/dominios/pendencias-ssl");
      return r.itens ?? [];
    } catch {
      return [];
    }
  },
  ver: (uuid: string) => api.get<Dominio>(`/dominios/${uuid}`),
  criar: (dados: DominioEntrada) => api.post<Dominio>("/dominios", dados),
  alterar: (uuid: string, dados: Partial<DominioEntrada>) =>
    api.patch<Dominio>(`/dominios/${uuid}`, dados),
  /**
   * O servidor exige `?confirmar=<nome do domínio>` e recusa com 409 enquanto
   * houver caixa ou alias. Quem chamava sem o parâmetro levava 409 sempre — o
   * botão do painel não apagava nada, e também não dizia por quê.
   */
  remover: (uuid: string, confirmar: string) =>
    api.delete<void>(`/dominios/${uuid}`, undefined, { consulta: { confirmar } }),
  /**
   * MX, SPF, DKIM, DMARC e PTR. É o que reduz suporte quando o cliente erra
   * o DNS.
   *
   * O tipo é DiagnosticoDns (o contêiner dos cinco), não ConferenciaDns (UM
   * exame). Estava errado desde a fase 3 e ninguém notou porque a rota nunca
   * chegou a ser registrada no servidor.
   */
  dns: (uuid: string) => api.get<DiagnosticoDns>(`/dominios/${uuid}/dns`),
  /**
   * ANTISPAM POR DOMÍNIO. Só revenda para cima — ver o comentário da rota.
   *
   * O efeito NÃO é instantâneo: isto escreve no banco, e quem transforma em
   * configuração é um laço de cinco minutos dentro do Rspamd. A tela avisa,
   * porque "mudei e não mudou nada" é o chamado que isso geraria.
   */
  antispam: (uuid: string) => api.get<AntispamDominio>(`/dominios/${uuid}/antispam`),
  definirAntispam: (uuid: string, limites: { quarentena: number | null; recusa: number | null }) =>
    api.put<void>(`/dominios/${uuid}/antispam`, limites),
  acrescentarNaLista: (uuid: string, e: { tipo: string; remetente: string; nota?: string }) =>
    api.post<{ uuid: string }>(`/dominios/${uuid}/antispam/listas`, e),
  removerDaLista: (uuid: string, entrada: string) =>
    api.delete<void>(`/dominios/${uuid}/antispam/listas/${entrada}`),
};

export const caixas = {
  // O parâmetro se chama `q`, não `busca`. O contrato sempre disse `q` e os
  // handlers sempre leram `q`; era este cliente que mandava `busca`, e o
  // servidor descartava um parâmetro que não conhece — sem erro nenhum. O
  // campo de filtro do painel simplesmente não filtrava nada.
  listar: (p?: Paginacao & { dominio?: string; q?: string }) =>
    api.get<PaginaDe<Caixa>>("/caixas", { consulta: p }),
  ver: (uuid: string) => api.get<Caixa>(`/caixas/${uuid}`),
  criar: (dados: CaixaEntrada) => api.post<Caixa>("/caixas", dados),
  alterar: (uuid: string, dados: Partial<CaixaEntrada>) =>
    api.patch<Caixa>(`/caixas/${uuid}`, dados),
  /**
   * `nova_senha`, e não `senha`: é o nome que o contrato declara. Estava
   * errado desde a fase 4 e ninguém notou porque a tela nunca chamou esta
   * rota — a redefinição de senha não existia no painel.
   *
   * `senha_atual` só é exigida quando a própria pessoa troca. O admin troca
   * sem ela, e nesse caso o servidor REVOGA todas as sessões e senhas de
   * aplicativo da caixa: a troca administrativa costuma ser resposta a
   * comprometimento, e deixar a sessão do invasor viva não resolveria nada.
   */
  trocarSenha: (uuid: string, nova_senha: string, senha_atual?: string) =>
    api.put<void>(`/caixas/${uuid}/senha`, { nova_senha, senha_atual }),
  /** `confirmar` é o endereço inteiro. Ver ConfirmarRemocao em painel/comuns. */
  remover: (uuid: string, confirmar: string) =>
    api.delete<void>(`/caixas/${uuid}`, undefined, { consulta: { confirmar } }),
  /**
   * ENTRAR NA CAIXA DO CLIENTE, sem a senha dele.
   *
   * A sessão que volta daqui é diferente de uma de login, e as diferenças
   * são travas do servidor, não desta camada: nível `usuario` qualquer que
   * seja o nível da caixa, 30 minutos fixos, e SEM token de renovação.
   *
   * Sem renovação é o que mantém a sessão de quem entrou intacta — nenhum
   * cookie é gravado — e é também o que faz recarregar a página encerrar a
   * personificação. Isso é recurso, não defeito: falha fechando.
   *
   * Fica registrado em auditoria ANTES de o token existir, no domínio da
   * caixa alvo, então o cliente vê no painel dele.
   */
  entrar: (uuid: string) => api.post<Sessao>(`/caixas/${uuid}/entrar`, {}),
};

export const aliases = {
  listar: (p?: Paginacao & { dominio?: string }) =>
    api.get<PaginaDe<Alias>>("/aliases", { consulta: p }),
  ver: (uuid: string) => api.get<Alias>(`/aliases/${uuid}`),
  criar: (dados: AliasEntrada) => api.post<Alias>("/aliases", dados),
  alterar: (uuid: string, dados: Partial<AliasEntrada>) =>
    api.patch<Alias>(`/aliases/${uuid}`, dados),
  remover: (uuid: string) => api.delete<void>(`/aliases/${uuid}`),
};

export const entregas = {
  listar: (
    p?: Paginacao & {
      tipo?: string;
      endereco?: string;
      /** Só o remetente. A direção é a pergunta — ver a API. */
      de?: string;
      /** Só o destinatário. */
      para?: string;
      desde?: string;
      ate?: string;
    },
  ) =>
    api.get<PaginaDe<EventoEntrega>>("/entregas", { consulta: p }),
  fila: (p?: Paginacao) => api.get<PaginaDe<ItemFila>>("/fila", { consulta: p }),
};

/**
 * A quarentena É a pasta Junk da caixa. Liberar move para a INBOX e ensina ao
 * antispam que aquilo era legítimo; descartar apaga em definitivo e ensina o
 * contrário. O aprendizado é por caixa, nunca global.
 */
export const quarentena = {
  /**
   * A página traz `sem_nota` além do `total`: quantas das retidas NUNCA
   * foram avaliadas pelo nosso filtro.
   *
   * Vem do servidor e não é contado aqui de propósito — contar na tela
   * alcança só a página carregada, e o aviso dizia "47 vieram de migração"
   * quando eram 13.053 de 13.126.
   */
  listar: (p?: Paginacao & { dominio?: string }) =>
    api.get<PaginaDe<MensagemQuarentena> & { sem_nota?: number }>("/quarentena", {
      consulta: p,
    }),
  liberar: (uuid: string) => api.post<void>(`/quarentena/${uuid}/liberar`),
  descartar: (uuid: string) => api.delete<void>(`/quarentena/${uuid}`),
};

export const auditoria = (p?: Paginacao & { acao?: string; ator?: string }) =>
  api.get<PaginaDe<RegistroAuditoria>>("/auditoria", { consulta: p });

/** O TXT do DKIM pronto para colar no DNS do cliente. */
export const dkimDoDominio = (uuid: string) =>
  api.get<{
    seletor: string;
    nome: string;
    tipo?: string;
    valor: string;
    publicado: boolean;
    aviso?: string;
  }>(`/dominios/${uuid}/dkim`);

/**
 * MIGRAÇÃO DE PAINEL — o painel ENFILEIRA, outro processo EXECUTA.
 *
 * A chave SSH não passa por aqui e não fica no banco. A API é o processo
 * exposto à internet; guardar nela a credencial root do servidor do cliente
 * seria o mesmo erro que dar a ela o socket do Docker — só que o estrago
 * sairia da nossa plataforma e alcançaria outra empresa.
 *
 * O que se guarda são as COORDENADAS: host, porta e usuário. A chave vive no
 * servidor e é montada apenas no processo que migra.
 */
export const migracao = {
  servidores: () =>
    api.get<{ itens: ServidorOrigem[] }>("/migracao/servidores"),
  /** O corpo vem do CONTRATO, não daqui. Ver ServidorOrigemEntrada. */
  cadastrarServidor: (dados: ServidorOrigemEntrada) =>
    api.post<ServidorOrigem>("/migracao/servidores", dados),
  removerServidor: (uuid: string) =>
    api.delete<void>(`/migracao/servidores/${uuid}`),

  trabalhos: () => api.get<{ itens: TrabalhoMigracao[] }>("/migracao/trabalhos"),
  /**
   * Sem `dominio`, o trabalho é descobrir quais a conta tem.
   *
   * `contas` cria o domínio, as caixas e os encaminhamentos — e exige a
   * revenda, que é a única coisa que o cPanel não sabe.
   */
  orfaos: () => api.get<{ itens: MaildirOrfao[] }>("/maildirs-orfaos"),
  liberarOrfao: (uuid: string) =>
    api.post<void>(`/maildirs-orfaos/${uuid}/liberar`, {}),

  enfileirar: (dados: {
    /**
     * Opcional no caminho IMAP: lá o servidor pode ser DESCRITO em
     * `origem_host`, e a API o registra sozinha. Obrigar a cadastrar antes
     * seria um passo que só existe por causa do nosso modelo de dados.
     */
    servidor_uuid?: string;
    conta?: string;
    dominio?: string;
    estagio?:
      | "dominios"
      | "conferir"
      | "completo"
      | "contas"
      | "mensagens"
      | "contatos"
      | "verificar"
      | "liberar"
      // Põe o índice do webmail em dia num domínio já migrado. Só lê do
      // Dovecot e escreve no índice — não toca na origem nem na caixa.
      | "indexar"
      // Caminho IMAP, para origem que não é cPanel.
      | "testar"
      | "imap";
    revenda_uuid?: string;
    caixa?: string;
    /** Só no caminho IMAP: o servidor de onde as mensagens vêm. */
    origem_host?: string;
    origem_porta?: number;
    /** Só no caminho IMAP: o login na origem. */
    origem_usuario?: string;
    /**
     * Só no caminho IMAP: a senha da caixa NA ORIGEM.
     *
     * Ela sai daqui e é SELADA pela API antes de tocar o banco — a API sabe
     * fechar e não sabe abrir. O destino não pede senha: é o nosso servidor,
     * e o trabalhador entra com a chave-mestra do Dovecot.
     */
    origem_senha?: string;
  }) => api.post<TrabalhoMigracao>("/migracao/trabalhos", dados),
  removerTrabalho: (uuid: string) =>
    api.delete<void>(`/migracao/trabalhos/${uuid}`),
};

/** Um bloqueio do limitador de login. Ver api/interno/web/bloqueios.go. */
export interface BloqueioLogin {
  uuid: string;
  tipo: "conta" | "ip";
  alvo: string;
  dominio?: string;
  ip?: string;
  tentativas: number;
  criado_em: string;
  expira_em: string;
  ativo: boolean;
  liberado_em?: string;
  liberado_por?: string;
}

export const bloqueios = {
  listar: async (p?: { ativos?: string; alvo?: string }): Promise<BloqueioLogin[]> => {
    const r = await api.get<{ itens?: BloqueioLogin[] }>("/bloqueios", { consulta: p });
    return r.itens ?? [];
  },
  /** Destrava antes da hora. Limpa a contagem em memória, que é o que vale. */
  liberar: (uuid: string) => api.post<void>(`/bloqueios/${uuid}/liberar`),
};

/** Uma chave de API. O segredo nunca vem — só o prefixo, que é público. */
export interface ChaveApi {
  uuid: string;
  nome: string;
  prefixo: string;
  revenda: string;
  revenda_uuid: string;
  /** `super_admin` alcança todas as revendas (agente de suporte). */
  nivel: "revenda" | "super_admin";
  criada_por: string;
  criado_em: string;
  usada_em?: string;
  ativa: boolean;
  revogada_em?: string;
}

export const chavesApi = {
  listar: async (): Promise<ChaveApi[]> => {
    const r = await api.get<{ itens?: ChaveApi[] }>("/chaves-api");
    return r.itens ?? [];
  },
  /**
   * A resposta traz a chave INTEIRA — a única vez que ela existe.
   * `super_admin` dispensa a revenda: a API assina com a de quem cria.
   */
  criar: (dados: { nome: string; nivel: "revenda" | "super_admin"; revenda_uuid?: string }) =>
    api.post<{ uuid: string; nome: string; prefixo: string; nivel: string; chave: string }>(
      "/chaves-api",
      dados.nivel === "super_admin" ? { nome: dados.nome, nivel: dados.nivel } : dados,
    ),
  revogar: (uuid: string) => api.delete<void>(`/chaves-api/${uuid}`),
};

// ---------------------------------------------------------------------------
// SEGURANÇA DE IP — abusos, regra automática e bloqueio.
//
// Só super admin: IP não pertence a revenda. Um endereço pode ser a saída NAT
// de um escritório com caixas de dois clientes diferentes, e deixar uma
// revenda bloqueá-lo seria deixá-la derrubar o cliente da outra.

export interface AbusoIP {
  ip: string;
  falhas: number;
  /** Maior que zero significa CLIENTE, por mais que erre. */
  sucessos: number;
  contas_tentadas: number;
  contas: string[];
  nunca_bloquear: boolean;
  primeira_em: string;
  ultima_em: string;
  veredito: string;
  explicacao: string;
  bloqueado: boolean;
  bloqueio?: {
    uuid: string;
    motivo: string;
    bloqueado_por: string;
    automatico: boolean;
    expira_em?: string | null;
  };
}

export interface RegraBloqueio {
  ativa: boolean;
  min_contas: number;
  min_falhas: number;
  horas_bloqueio: number;
  alterado_por: string;
  alterado_em: string;
  /** A trava que NÃO é editável, em texto, para a tela poder mostrá-la. */
  trava: string;
}

export const seguranca = {
  abusos: (filtro: { so_suspeitos?: boolean; pagina?: number; por_pagina?: number }) =>
    api.get<{
      itens?: AbusoIP[];
      total?: number;
      por_pagina?: number;
      total_paginas?: number;
    }>("/seguranca/abusos", { consulta: filtro }),

  regra: () => api.get<RegraBloqueio>("/seguranca/regra-bloqueio"),

  salvarRegra: (dados: Partial<RegraBloqueio>) =>
    api.put<RegraBloqueio>("/seguranca/regra-bloqueio", dados),

  bloquear: (ip: string, motivo: string, horas: number) =>
    api.post<{ uuid: string; ip: string; alcance: string }>(
      "/seguranca/bloqueios-ip",
      { ip, motivo, horas },
    ),

  desbloquear: (uuid: string) =>
    api.delete<{ ip: string; era_automatico: boolean; excecao_marcada: boolean }>(
      `/seguranca/bloqueios-ip/${encodeURIComponent(uuid)}`,
    ),
};
